import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    BANDS_DIRECT_INTENT_STORAGE_KEY,
    BANDS_DEFAULT_VIEW_MODE,
    BANDS_SELECTED_BAND_OBJECT_STORAGE_KEY,
    BANDS_SELECTED_CHAT_STORAGE_KEY,
    BANDS_VIEW_MODES,
    BANDS_VIEW_MODE_STORAGE_KEY,
    BANDS_VIEW_MODE_WELCOME,
    BANDS_VIEW_MODE_WORKSPACE,
} from "../../../constants/bands";
import { BANDS_TAB_ID } from "../../../constants/MainNavigationTabSpecs";
import { NAVIGATE_TAB_EVENT } from "../../../constants/navigation";
import {
    readSessionViewMode,
    writeSessionViewMode,
} from "../../../utils/viewModeStorage";
import { buildBandsPath, isBandsRoutePath } from "../../../utils/bandRoutes";
import BandsWelcomePage from "../BandsWelcomePage";
import { BandsWorkspacePage } from "./BandsWorkspacePage";

const readJsonFromLocalStorage = (storageKey) => {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const raw = window.localStorage.getItem(storageKey);
        return raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
};

const hasValidStoredViewMode = () => {
    if (typeof window === "undefined") {
        return false;
    }
    try {
        return BANDS_VIEW_MODES.includes(window.sessionStorage.getItem(BANDS_VIEW_MODE_STORAGE_KEY));
    } catch (_) {
        return false;
    }
};

const isDirectChatSelection = (payload) => {
    if (!payload || typeof payload !== "object" || payload.type !== "dm") {
        return false;
    }
    const directChatId = payload.directChatId ?? payload.chatId ?? payload.id;
    return directChatId !== undefined && directChatId !== null && String(directChatId).trim().length > 0;
};

const isBandObjectSelection = (payload) => {
    if (!payload || typeof payload !== "object") {
        return false;
    }
    const bandObjectId = payload.bandObjectId ?? payload.objectId ?? payload.id;
    return bandObjectId !== undefined && bandObjectId !== null && String(bandObjectId).trim().length > 0;
};

const hasPendingDirectChatIntent = () => (
    isDirectChatSelection(readJsonFromLocalStorage(BANDS_DIRECT_INTENT_STORAGE_KEY))
);

const clearPendingDirectChatIntent = () => {
    if (typeof window !== "undefined") {
        try {
            window.localStorage.removeItem(BANDS_DIRECT_INTENT_STORAGE_KEY);
        } catch (_) {
            // Best-effort cleanup only.
        }
    }
};

const hasPendingSelectedDirectChat = () => (
    isDirectChatSelection(readJsonFromLocalStorage(BANDS_SELECTED_CHAT_STORAGE_KEY))
);

const hasPendingSelectedBandObject = () => (
    isBandObjectSelection(readJsonFromLocalStorage(BANDS_SELECTED_BAND_OBJECT_STORAGE_KEY))
);

const readInitialViewMode = () => {
    const storedViewMode = readSessionViewMode(
        BANDS_VIEW_MODE_STORAGE_KEY,
        BANDS_DEFAULT_VIEW_MODE,
        BANDS_VIEW_MODES,
    );
    if (hasPendingDirectChatIntent()) {
        return BANDS_VIEW_MODE_WORKSPACE;
    }
    if (hasPendingSelectedBandObject()) {
        return BANDS_VIEW_MODE_WORKSPACE;
    }
    if (!hasValidStoredViewMode() && hasPendingSelectedDirectChat()) {
        return BANDS_VIEW_MODE_WORKSPACE;
    }
    return storedViewMode;
};

/**
 * Top-level Bands route. Toggles between the public discovery view (welcome)
 * and the user's authoring workspace. The active view persists per browser
 * session via `sessionStorage` so navigating away and back keeps the user
 * where they left off.
 *
 * Default = welcome (discovery). The workspace is reached via the selected
 * Band page's "Band managment" action, and exited via the small
 * "Discover bands" pill rendered at the top of the workspace.
 */
export function BandsPage() {
    const location = useLocation();
    const hasNestedBandPath = useMemo(() => {
        const pathname = typeof location.pathname === "string"
            ? (location.pathname.replace(/\/+$/, "") || "/")
            : "";
        return pathname !== buildBandsPath() && isBandsRoutePath(pathname);
    }, [location.pathname]);
    const [viewMode, setViewMode] = useState(() => (
        hasNestedBandPath ? BANDS_VIEW_MODE_WELCOME : readInitialViewMode()
    ));

    useEffect(() => {
        if (hasNestedBandPath) {
            setViewMode(BANDS_VIEW_MODE_WELCOME);
        }
    }, [hasNestedBandPath]);

    useEffect(() => {
        writeSessionViewMode(
            BANDS_VIEW_MODE_STORAGE_KEY,
            viewMode,
            BANDS_DEFAULT_VIEW_MODE,
            BANDS_VIEW_MODES,
        );
        if (viewMode === BANDS_VIEW_MODE_WORKSPACE) {
            clearPendingDirectChatIntent();
        }
    }, [viewMode]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }
        const handleNavigationIntent = (event) => {
            if (event?.detail?.tabId !== BANDS_TAB_ID) {
                return;
            }
            const eventPath = event?.detail?.path;
            if (typeof eventPath === "string" && eventPath !== buildBandsPath() && isBandsRoutePath(eventPath)) {
                setViewMode(BANDS_VIEW_MODE_WELCOME);
                return;
            }
            const hasDirectIntent = hasPendingDirectChatIntent();
            if (hasDirectIntent || hasPendingSelectedBandObject() || hasPendingSelectedDirectChat()) {
                if (hasDirectIntent) {
                    clearPendingDirectChatIntent();
                }
                setViewMode(BANDS_VIEW_MODE_WORKSPACE);
            }
        };
        window.addEventListener(NAVIGATE_TAB_EVENT, handleNavigationIntent);
        return () => {
            window.removeEventListener(NAVIGATE_TAB_EVENT, handleNavigationIntent);
        };
    }, []);

    const showWorkspace = useCallback(() => setViewMode(BANDS_VIEW_MODE_WORKSPACE), []);
    const showWelcome = useCallback(() => setViewMode(BANDS_VIEW_MODE_WELCOME), []);

    if (viewMode === BANDS_VIEW_MODE_WORKSPACE) {
        return <BandsWorkspacePage onReturnToWelcome={showWelcome} />;
    }
    return <BandsWelcomePage onOpenMyBand={showWorkspace} />;
}

export default BandsPage;
